import React, { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DrawerBody } from '@fiverr-private/overlay';
import { Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Link, Text } from '@fiverr-private/typography';
import { CompanyStatus, EmploymentType, WorkExperience } from '../../../../gql-operations/gql-generated';
import { useMonitoring } from '../../../../utils/monitoring';
import { useSellerData } from '../../../../hooks/useSellerData';
import { FederatedSeller } from '../../../../types';
import { T_KEY, workExperienceChronologicalComparator } from '../../utils';
import {
    CurrentViewState,
    TemporaryWorkExperience,
    WorkExperienceFormType,
    WorkExperienceViewStates,
} from '../../shared/types';
import workExperienceBIEvents from '../../utils/biEvents';
import TitleField from './TitleField';
import EmploymentTypeField from './EmploymentTypeField';
import CompanyField from './CompanyField';
import CurrentlyWorkingField from './CurrentlyWorkingField';
import DatesField from './DatesField/DatesField';
import DescriptionField from './DescriptionField';
import SkillsField from './SkillsField';
import IndustryField from './IndustryField';
import FormButtons from './FormButtons';

interface WorkExperienceFormProps {
    currentExperience?: WorkExperience;
    currentViewState?: CurrentViewState;
    setCurrentViewState: Dispatch<SetStateAction<CurrentViewState>>;
    setCurrentWorkExperiences: (workExperiences: TemporaryWorkExperience[] | undefined | null) => void;
    currentWorkExperiences?: TemporaryWorkExperience[] | undefined | null;
}

const WorkExperienceForm: FC<WorkExperienceFormProps> = ({
    currentExperience,
    currentWorkExperiences,
    currentViewState,
    setCurrentViewState,
    setCurrentWorkExperiences,
}) => {
    const seller = useSellerData(selectSeller);
    const initialIsDirty = useRef(currentViewState?.isDirty);
    const { Providers, fireEvents } = useMonitoring();

    const {
        register,
        handleSubmit,
        watch,
        control,
        setValue,
        setError,
        clearErrors,
        getValues,
        trigger,
        formState: { errors, defaultValues, isDirty: isFormDirty },
    } = useForm<WorkExperienceFormType>({
        defaultValues: currentExperience
            ? {
                  title: currentExperience.title,
                  employmentType:
                      currentExperience.employmentType === EmploymentType.Unspecified
                          ? undefined
                          : currentExperience.employmentType,
                  company: currentExperience?.company,
                  startMonth: currentExperience?.startDate
                      ? new Date(currentExperience?.startDate)?.getMonth().toString()
                      : '',
                  endMonth: currentExperience?.endDate
                      ? new Date(currentExperience?.endDate)?.getMonth().toString()
                      : '',
                  startYear: currentExperience?.startDate
                      ? new Date(currentExperience?.startDate)?.getFullYear().toString()
                      : '',
                  endYear: currentExperience?.endDate
                      ? new Date(currentExperience?.endDate)?.getFullYear().toString()
                      : '',
                  startDate: currentExperience?.startDate ? new Date(currentExperience?.startDate) : undefined,
                  endDate: currentExperience?.endDate ? new Date(currentExperience.endDate) : undefined,
                  currentlyWorking: !currentExperience?.endDate,
                  description: currentExperience?.description || '',
                  skills: currentExperience?.skills,
                  industry: currentExperience?.company?.industry || '',
              }
            : {},
    });

    useEffect(() => {
        setCurrentViewState((currentViewState) => {
            const actualDirty = currentViewState.isDirty || isFormDirty;
            return { ...currentViewState, isDirty: actualDirty };
        });
    }, [setCurrentViewState, isFormDirty]);

    const [currentlyWorking, description] = watch(['currentlyWorking', 'description']);

    const onSubmitBI = (position: number, isDelete = false) => {
        fireEvents({
            [Providers.BigQuery]: workExperienceBIEvents.clickedOnEditWorkExperience({
                sellerId: seller.user.id,
                state: isDelete ? 'delete' : 'save',
                position,
                ...(isDelete
                    ? {}
                    : {
                          companyId: getValues('company')?.id,
                          companyName: getValues('company')?.name,
                          startDate: getValues('startDate')?.valueOf(),
                          endDate: getValues('endDate')?.valueOf(),
                      }),
            }),
        });
    };

    const onSubmit: SubmitHandler<WorkExperienceFormType> = (data: WorkExperienceFormType) => {
        setCurrentViewState((state) => ({ ...state, viewState: WorkExperienceViewStates.List }));

        const updatedWorkExperience = {
            ...data,
            startDate: data.startDate?.valueOf(),
            endDate: data.currentlyWorking ? null : data.endDate?.valueOf(),
            currentJob: data.currentlyWorking,
            company: {
                ...data.company,
                ...(data.company.status !== CompanyStatus.Approved && { industry: data.industry }),
            },
        };

        const updatedWorkExperiences = currentExperience
            ? currentWorkExperiences?.map((experience) =>
                  experience.id === currentExperience.id ? { ...experience, ...updatedWorkExperience } : experience
              ) || []
            : [
                  ...(currentWorkExperiences || []),
                  {
                      ...updatedWorkExperience,
                      id: new Date().valueOf().toString(),
                      isNew: true,
                  },
              ];

        const sortedExperiences = updatedWorkExperiences.sort(workExperienceChronologicalComparator);
        setCurrentWorkExperiences(sortedExperiences);
        onSubmitBI(updatedWorkExperiences?.findIndex((experience) => experience.id === currentExperience?.id) || 0);
    };

    const handleSaveClick = () => {
        handleSubmit(onSubmit)();
    };

    const handleDeleteClick = () => {
        const position =
            currentWorkExperiences?.findIndex((experience) => experience.id === currentExperience?.id) || 0;
        onSubmitBI(position, true);
    };

    return (
        <>
            <DrawerBody>
                <Text marginBottom="8">
                    <I18n
                        k={`${T_KEY}.subtitle`}
                        templates={{
                            link: (text) => (
                                <Link
                                    href="https://help-pro.fiverr.com/hc/en-us/articles/21869947546257"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {text}
                                </Link>
                            ),
                        }}
                    />
                </Text>
                <Stack gap="4" direction="column" data-testid="work-experience-form">
                    <TitleField control={control} errors={errors} />
                    <EmploymentTypeField control={control} />
                    <CompanyField
                        control={control}
                        errors={errors}
                        trigger={trigger}
                        getValues={getValues}
                        setValue={setValue}
                    />
                    <CurrentlyWorkingField register={register} defaultValues={defaultValues} />
                    <DatesField
                        currentlyWorking={currentlyWorking}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        setValue={setValue}
                        trigger={trigger}
                        control={control}
                        errors={errors}
                    />
                    <DescriptionField
                        register={register}
                        defaultValues={defaultValues}
                        description={description}
                        errors={errors}
                    />
                    <SkillsField control={control} />
                    <IndustryField control={control} getValues={getValues} />
                </Stack>
            </DrawerBody>
            <FormButtons
                handleSaveClick={handleSaveClick}
                handleDeleteClick={handleDeleteClick}
                initialIsDirty={initialIsDirty.current}
                setCurrentViewState={setCurrentViewState}
                currentExperience={currentExperience}
                currentWorkExperiences={currentWorkExperiences}
                setCurrentWorkExperiences={setCurrentWorkExperiences}
            />
        </>
    );
};

function selectSeller(seller: FederatedSeller) {
    return seller;
}

export default WorkExperienceForm;
